import { AppSort, CommandModel } from "@/app/entities";
import { CommandeFilterDataModel } from "@/components/molecules/commande";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { format, parse } from "date-fns";
import frLocale from "date-fns/locale/fr";

export type CommandeStoreType = {
  commandes: CommandModel[];
  savedCommandes: CommandModel[];
  paginatedItems: {
    content: CommandModel[];
    empty: boolean;
    first: boolean;
    last: boolean;
    number: number;
    numberOfElements: number;
    pageable: object;
    size: number;
    sort: object;
    totalElements: number;
    totalPages: number;
  };

  sort?: AppSort;
  setSort: (sort: AppSort) => void;
  filterForm: CommandeFilterDataModel;
  setCommandes: (commandes: CommandModel[]) => void;
  setFilterForm: (filterForm: CommandeFilterDataModel) => void;
  getCommandeByReference: (reference: string) => CommandModel | undefined;
  filterData: (filterForm: CommandeFilterDataModel) => CommandModel[];
  clearStore: () => void;
  setPaginatedItems: (data: any) => void;
};

export const useCommandeStore = create<CommandeStoreType>()(
  persist(
    (set, get) => ({
      get commandes() {
        return get().paginatedItems.content;
      },
      savedCommandes: [],
      paginatedItems: {
        content: [],
        empty: true,
        first: true,
        last: true,
        number: 0,
        numberOfElements: 0,
        pageable: {},
        size: 0,
        sort: {},
        totalElements: 0,
        totalPages: 0,
      },
      filterForm: {
        status: "",
        creationDate: "",
        dfs: "",
        draft: "",
        shelve: "",
      },
      setSort: (sort: AppSort) => {
        set((state) => ({
          ...state,
          sort,
        }));
      },
      setFilterForm: (filterForm: CommandeFilterDataModel) => {
        set((state) => ({
          ...state,
          filterForm,
        }));
      },
      setCommandes: (commandes: CommandModel[]) => {
        set((state) => ({
          ...state,
          savedCommandes: commandes,
          paginatedItems: {
            ...state.paginatedItems,
            content: commandes,
          },
        }));
      },
      getCommandeByReference: (reference: string) =>
        get().paginatedItems.content.find(
          (commande: CommandModel) => commande.reference === reference
        ),
      setPaginatedItems: (data: any) => {
        set((state) => ({
          ...state,
          paginatedItems: { ...data },
        }));
      },
      filterData: (filterForm: CommandeFilterDataModel) => {
        let _commandes = [...get().savedCommandes];
        if (filterForm.status.trim()) {
          _commandes = _commandes.filter(
            (item: CommandModel) => item.statut === filterForm.status
          );
        }

        if (filterForm.creationDate.trim()) {
          const canUseCreationDateFilter = format(
            parse(filterForm.creationDate.trim(), "dd/MM/yyyy", new Date()),
            "dd LLLL yyyy",
            { locale: frLocale }
          );
          _commandes = _commandes.filter((item: CommandModel) => {
            return (item.dateCreation ?? "").includes(canUseCreationDateFilter);
          });
        }

        if (filterForm.dfs.trim()) {
          const canUsedfsFilter = format(
            parse(filterForm.dfs.trim(), "dd/MM/yyyy", new Date()),
            "dd LLLL yyyy",
            { locale: frLocale }
          );
          _commandes = _commandes.filter((item: CommandModel) => {
            return (item.dfs ?? "").includes(canUsedfsFilter);
          });
        }

        return _commandes;
      },
      clearStore: () => {
        set({
          sort: undefined,
          savedCommandes: [],
          paginatedItems: {
            content: [],
            empty: true,
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            pageable: {},
            size: 0,
            sort: {},
            totalElements: 0,
            totalPages: 0,
          },
          filterForm: {
            status: "",
            creationDate: "",
            dfs: "",
            shelve: "",
          },
        });
        localStorage.removeItem("commande-store");
      },
    }),
    {
      name: "commande-store",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
